<template>
	<ui-modal @toggle="$emit('toggle')">
		<div class="flex flex-col gap-4">
			<ui-title textSize="big" textPosition="left" class="lg:col-span-2 text-slate-700">
				<template v-slot:text>
					<p>Rapport sur les contacts suivis</p>
				</template>
			</ui-title>

			<div class="flex flex-col gap-3">
				<div v-for="(period, index) in periods" :key="index" class="rounded-lg border border-slate-200 bg-slate-50 p-3">
					<div class="flex items-center justify-between mb-2">
						<span class="text-sm font-medium text-slate-600">Période {{ index + 1 }}</span>
						<button type="button" v-if="periods.length > 1" @click="removePeriod(index)" class="text-xs text-red-500 hover:text-red-700">
							Supprimer
						</button>
					</div>

					<div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
						<div class="flex flex-col gap-1">
							<label class="text-xs font-medium text-slate-600">Début</label>
							<div class="grid grid-cols-2 gap-2">
								<select v-model="period.startMonth" class="border rounded-md px-2 py-2 text-sm">
									<option value="">Mois</option>
									<option v-for="month in months" :key="month.value" :value="month.value">
										{{ month.label }}
									</option>
								</select>
								<select v-model="period.startYear" class="border rounded-md px-2 py-2 text-sm">
									<option value="">Année</option>
									<option v-for="year in years" :key="year" :value="year">{{ year }}</option>
								</select>
							</div>
						</div>

						<div class="flex flex-col gap-1">
							<label class="text-xs font-medium text-slate-600">Fin</label>
							<div class="grid grid-cols-2 gap-2">
								<select v-model="period.endMonth" class="border rounded-md px-2 py-2 text-sm">
									<option value="">Mois</option>
									<option v-for="month in months" :key="month.value" :value="month.value">
										{{ month.label }}
									</option>
								</select>
								<select v-model="period.endYear" class="border rounded-md px-2 py-2 text-sm">
									<option value="">Année</option>
									<option v-for="year in years" :key="year" :value="year">{{ year }}</option>
								</select>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="flex justify-start">
				<ui-button @click="addPeriod">
					<template v-slot:icon><i class="fas fa-plus"></i></template>
					<template v-slot:text>Ajouter une période</template>
				</ui-button>
			</div>

			<ui-button @click="generateContactsReport()" :loading="loading">
				<template v-slot:icon><i class="fas fa-file-download"></i></template>
				<template v-slot:text>Télécharger le rapport</template>
			</ui-button>
			<form-validation-errors :errors="errors" class="lg:col-span-2"></form-validation-errors>
		</div>
	</ui-modal>
</template>

<script setup>
	import { ref, computed } from 'vue';
	import { usePage } from '@inertiajs/vue3';
	import { toast } from 'vue3-toastify';

	const props = defineProps({
		type: {
			type: String,
			default: 'associated',
		},
	});

	const page = usePage();
	const emit = defineEmits(['toggle']);

	const loading = ref(false);
	const name = ref('Rapport');
	const errors = ref({});

	const months = [
		{ value: '01', label: 'Janvier' },
		{ value: '02', label: 'Février' },
		{ value: '03', label: 'Mars' },
		{ value: '04', label: 'Avril' },
		{ value: '05', label: 'Mai' },
		{ value: '06', label: 'Juin' },
		{ value: '07', label: 'Juillet' },
		{ value: '08', label: 'Août' },
		{ value: '09', label: 'Septembre' },
		{ value: '10', label: 'Octobre' },
		{ value: '11', label: 'Novembre' },
		{ value: '12', label: 'Décembre' },
	];

	const years = computed(() => {
		const currentYear = new Date().getFullYear();
		return Array.from({ length: 20 }, (_, i) => currentYear - 19 + i).reverse();
	});

	const createEmptyPeriod = () => ({
		startMonth: '',
		startYear: '',
		endMonth: '',
		endYear: '',
	});

	const periods = ref([createEmptyPeriod()]);

	const addPeriod = () => {
		periods.value.push(createEmptyPeriod());
	};

	const removePeriod = (index) => {
		periods.value.splice(index, 1);
		if (periods.value.length === 0) {
			periods.value.push(createEmptyPeriod());
		}
	};

	const generateContactsReport = async () => {
		const getParameter = props.type === 'all' ? 'all' : page.props.auth.user.id;
		loading.value = true;
		errors.value = {};
		try {
			const response = await axios.get(route('contacts.report', getParameter), {
				params: { periods: periods.value },
				responseType: 'blob',
				headers: { Accept: 'application/json' },
			});
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `${name.value} - Rapport ${new Date().toLocaleDateString('fr-FR')}.xlsx`);
			document.body.appendChild(link);
			link.click();
		} catch (error) {
			if (error.response && error.response.status === 422) {
				try {
					const dataBlob = error.response.data;
					let parsed = {};

					if (dataBlob && typeof dataBlob.text === 'function') {
						const text = await dataBlob.text();
						parsed = JSON.parse(text || '{}');
					} else {
						parsed = error.response.data;
					}

					errors.value = parsed.errors || parsed || {};
				} catch (e) {
					errors.value = { error: ['Erreur de validation inconnue'] };
				}
			} else {
				toast.error('Erreur lors de la génération du rapport');
			}
		}
		loading.value = false;
	};
</script>
